/* eslint-disable no-underscore-dangle */
import { useQuery } from '@nerdwallet/apollo';
import appConfig from '@nerdwallet/app-config';
import { FinishType } from '@nerdwallet/react-apply-now';
import {
  GqlKetoProductCard,
  PREHEADER_TYPE,
  SkeletonKetoProductCard,
} from '@nerdwallet/react-marketplace-product-card';
import { usePathname } from 'next/navigation';
import P from 'prop-types';
import { Fragment, useMemo } from 'react';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import {
  segmentImpressionId,
  trackSegmentTrackEvent,
} from '~/app/lib/segment-analytics';
import { baseSegmentAnalytics } from '~/app/lib/utils';
import SegmentImpression from '../segment-impression';
import styles from './keto-product-card.module.scss';
import { marketplaceOffers } from './queries';
import WhyNerdsLoveIt from './WhyNerdsLoveIt';
import WhyThisProduct from './WhyThisProduct';

// we're importing this style as non module because we want to override some
// KetoProductCard styles, and we can't do that with module styles cuz it's scoped
import './personal-loans-style.scss';

const proAndConsRenderer = ({ children }) => (
  <div>{children?.props?.children?.props?.children}</div>
);

const driverRenderer = ({ children }) => <Fragment>{children}</Fragment>;

const getApplyLinkWithTrackingParams = (url = '', searchParams = []) => {
  try {
    const applyLinkWithTrackingParams = new URL(url);
    Object.keys(searchParams)?.forEach((key) => {
      applyLinkWithTrackingParams?.searchParams?.append(key, searchParams[key]);
    });
    return applyLinkWithTrackingParams.toString();
  } catch (error) {
    return url;
  }
};

const KetoStructuredCard = ({
  dataDependency,
  footerOptions = {},
  badgeProps,
  productOverride,
  driverOverrides = [],
  shouldOmitRating,
  pageViewId,
  driver: driverGroup,
  deviceType,
}) => {
  const identity = useIdentity();
  const productOfferId = dataDependency?.data?.ids?.[0]?.id;
  const pathname = usePathname();

  const { data = {}, loading } = useQuery(marketplaceOffers, {
    variables: { ids: productOfferId },
    fetchPolicy: 'no-cache',
  });

  const baseMarketplaceEntity = data?.marketplaceEntitiesV2?.[0];

  const segmentImpressionProps = {
    ...baseSegmentAnalytics,
    impression_id: segmentImpressionId,
    impression_pageview_id: pageViewId,
    product_impression_id: segmentImpressionId,
    sku_name: baseMarketplaceEntity?.displayName,
    sku_level: baseMarketplaceEntity?.entityType,
    sku_product_type: baseMarketplaceEntity?.productType,
    sku_source: 'marketplace',
    impression_page_url: pathname,
    sku_impression_id: segmentImpressionId,
    monetizable: 'YES_ASSUMED',
    finish_type: FinishType.EXTERNAL_APPLICATION,
    device_type: deviceType,
    institution_id: baseMarketplaceEntity?.institution?.id,
    institution_name: baseMarketplaceEntity?.institution?.name,
    product_id: baseMarketplaceEntity?.product?.id,
    product_name: baseMarketplaceEntity?.product?.name,
    offer_id: baseMarketplaceEntity?.id,
    offer_name: baseMarketplaceEntity?.name,
  };

  const applyNowLink =
    getApplyLinkWithTrackingParams(
      baseMarketplaceEntity?.cta?.link,
      segmentImpressionProps
    ) || baseMarketplaceEntity?.cta?.link;

  const qualificationsBullets = useMemo(
    () =>
      baseMarketplaceEntity?.qualificationsBullets?.map(
        (bullet) => `• ${bullet}`
      ) || [],
    [baseMarketplaceEntity?.qualificationsBullets]
  );

  const feeBullets = useMemo(
    () =>
      baseMarketplaceEntity?.feesBullets?.map((bullet) => `• ${bullet}`) || [],
    [baseMarketplaceEntity?.feesBullets]
  );

  const marketplaceEntity = useMemo(() => {
    const personalLoansMarketingBullets = [
      '<strong>Qualifications</strong>',
      ...qualificationsBullets,

      '<strong>Available Term Lengths</strong>',
      `• ${baseMarketplaceEntity?.availableTermLengths || ''}`,

      '<strong>Fees</strong>',
      ...feeBullets,
      `Disclaimer: ${baseMarketplaceEntity?.disclaimer}` || '',
    ];

    const rewardsPlacementDriversType = [
      {
        id: baseMarketplaceEntity?.driverAnnualFee?.id,
        label: baseMarketplaceEntity?.driverAnnualFee?.label,
        value: baseMarketplaceEntity?.driverAnnualFee?.value,
        valueTooltip:
          baseMarketplaceEntity?.driverAnnualFee?.valueTooltip || '',
      },
      {
        id: baseMarketplaceEntity?.driverIntroApr?.id,
        label: baseMarketplaceEntity?.driverIntroApr?.label,
        value: baseMarketplaceEntity?.driverIntroApr?.value,
        valueTooltip: baseMarketplaceEntity?.driverIntroApr?.valueTooltip || '',
      },
      {
        id: baseMarketplaceEntity?.driverRegularApr?.id,
        label: baseMarketplaceEntity?.driverRegularApr?.label,
        value: baseMarketplaceEntity?.driverRegularApr?.value,
        valueTooltip:
          baseMarketplaceEntity?.driverRegularApr?.valueTooltip || '',
      },
    ];

    const marketplaceEntityObject = {
      ...baseMarketplaceEntity,
      recommendedCreditScoreRangeDetails:
        baseMarketplaceEntity?.recommendedCreditScoreRangeDetails || {},
      displayName:
        productOverride?.heading || baseMarketplaceEntity?.displayName,
      cta: {
        ...baseMarketplaceEntity?.cta,
        link: applyNowLink,
        text: productOverride?.ctaText || baseMarketplaceEntity?.cta?.text,
      },

      // workaround for personal loans marketing bullets once "personal_loans"
      // does not have a marketing bullets field for the time being
      marketingBullets:
        baseMarketplaceEntity?.productType === 'personal_loans'
          ? personalLoansMarketingBullets
          : baseMarketplaceEntity?.marketingBullets,
    };

    // if the driver group is "APR_GROUP" and is a credit_cards type, we override the placement drivers with the rewards type
    if (
      driverGroup === 'APR_GROUP' &&
      baseMarketplaceEntity?.productType === 'credit_cards'
    ) {
      marketplaceEntityObject.placementDrivers = rewardsPlacementDriversType;
    }

    // if the driver group is "DEFAULT_DRIVER_GROUP" we override the placement drivers with the driverOverrides
    if (driverOverrides?.length) {
      marketplaceEntityObject.placementDrivers = driverOverrides.map(
        (driver) => ({
          id: driver?.$unique_key,
          valueTooltip: driver?.tooltip,
          value: driver?.text || '',
          label: driver?.label,
        })
      );
    }
    return marketplaceEntityObject;
  }, [
    qualificationsBullets,
    baseMarketplaceEntity,
    feeBullets,
    productOverride?.heading,
    productOverride?.ctaText,
    applyNowLink,
    driverGroup,
    driverOverrides,
  ]);

  const {
    includeWhyThisProduct,
    includeWhyNerdsLoveIt,
    whyThisProductMethodologyUrl,
    whyThisProductText,
    whyThisProductTypeOverride,
    whyNerdsLoveItContainer,
    whyNerdsLoveItTitle,
    whyNerdsLoveItTitleIconUrl,
    whyNerdsLoveItText,
    whyNerdsLoveItMethodologyUrl,
    whyNerdsLoveItAvatarUrl,
    turnOnGroupedBorders,
  } = footerOptions;

  const shouldOmitCreditScore =
    marketplaceEntity?.productType !== 'credit_cards';

  const postApplyOnClick = () => {
    trackSegmentTrackEvent('Element Interaction', {
      entity_name: marketplaceEntity?.name,
      impression_id: segmentImpressionId,
      impression_pageview_id: pageViewId,
      product_impression_id: segmentImpressionId,
      sku_name: baseMarketplaceEntity?.displayName,
      sku_level: baseMarketplaceEntity?.entityType,
      sku_product_type: baseMarketplaceEntity?.productType,
      sku_source: 'marketplace',
      impression_page_url: pathname,
      sku_impression_id: segmentImpressionId,
      monetizable: 'YES_ASSUMED',
      finish_type: FinishType.EXTERNAL_APPLICATION,
      device_type: deviceType,
      institution_id: baseMarketplaceEntity?.institution?.id,
      institution_name: baseMarketplaceEntity?.institution?.name,
      product_id: baseMarketplaceEntity?.product?.id,
      product_name: baseMarketplaceEntity?.product?.name,
      offer_id: baseMarketplaceEntity?.id,
      offer_name: baseMarketplaceEntity?.name,
      location: pathname,
      cta_type: 'apply_now_button',
      calling_deployable_name: appConfig?._meta?.deployable,
      calling_deployable_version: appConfig?._meta?.version,
      ...baseSegmentAnalytics,
    });
  };

  const personalLoansStyle =
    marketplaceEntity?.productType === 'personal_loans'
      ? 'personalLoansProductDetailsTab'
      : '';

  const isCardInactive = baseMarketplaceEntity?.status !== 'ACTIVE';

  // if the card is inactive, we don't render anything, this way the display
  // of this card can be controlled by Marketplace Admin
  if (!loading && isCardInactive) return null;

  const Wrapper = baseMarketplaceEntity?.id ? SegmentImpression : Fragment;

  const wrapperProps = baseMarketplaceEntity?.id
    ? {
        eventName: 'SKU Viewed',
        eventProps: segmentImpressionProps,
      }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      <div className={styles.ketoProductCard}>
        {loading ? (
          <SkeletonKetoProductCard />
        ) : (
          <GqlKetoProductCard
            // once "Product Details" tab is designed for "credit_cards"type
            // we add some styles to get a better output
            className={personalLoansStyle}
            driversRenderer={Fragment}
            driverRenderer={driverRenderer}
            gqlMarketplaceEntity={marketplaceEntity}
            identity={identity}
            shouldOmitCreditScoreDriver={shouldOmitCreditScore}
            shouldOmitRating={shouldOmitRating}
            consRenderer={proAndConsRenderer}
            prosRenderer={proAndConsRenderer}
            riaReviewDateInfo={{
              periodConsideredDate: marketplaceEntity?.periodConsideredDate,
              reviewedAtDate: marketplaceEntity?.reviewedAtDate,
            }}
            preheader={{
              text: badgeProps?.badgeText,
              type: PREHEADER_TYPE.BADGE_2025,
            }}
            postApplyOnClick={postApplyOnClick}
          />
        )}
        {includeWhyThisProduct && (
          <WhyThisProduct
            whyThisProductText={whyThisProductText}
            whyThisProductTypeOverride={whyThisProductTypeOverride}
            whyThisProductMethodologyUrl={whyThisProductMethodologyUrl}
          />
        )}
        {includeWhyNerdsLoveIt && (
          <WhyNerdsLoveIt
            container={whyNerdsLoveItContainer}
            whyNerdsLoveItMethodologyUrl={whyNerdsLoveItMethodologyUrl}
            turnOnGroupedBorders={turnOnGroupedBorders}
            whyNerdsLoveItText={whyNerdsLoveItText}
            whyNerdsLoveItTitle={whyNerdsLoveItTitle}
            whyNerdsLoveItTitleIconUrl={whyNerdsLoveItTitleIconUrl}
            whyNerdsLoveItAvatarUrl={whyNerdsLoveItAvatarUrl}
          />
        )}
      </div>
    </Wrapper>
  );
};

KetoStructuredCard.propTypes = {
  dataDependency: P.shape({
    data: P.shape({ ids: P.arrayOf(P.string) }),
    type: P.string,
  }),
  footerOptions: P.shape({
    whyThisProductMethodologyUrl: P.string,
    whyThisProductText: P.string,
    whyThisProductTypeOverride: P.string,
    whyNerdsLoveItContainer: P.string,
    whyNerdsLoveItTitle: P.string,
    whyNerdsLoveItTitleIconUrl: P.string,
    whyNerdsLoveItText: P.string,
    whyNerdsLoveItMethodologyUrl: P.string,
    includeWhyThisProduct: P.bool,
    includeWhyNerdsLoveIt: P.bool,
    turnOnGroupedBorders: P.bool,
    whyNerdsLoveItAvatarUrlL: P.string,
  }),
  badgeProps: P.shape({
    badgeText: P.string,
  }),
  productOverride: P.shape({
    heading: P.string,
    ctaText: P.string,
  }),
  driver: P.string,
  driverOverrides: P.array,
  shouldOmitRating: P.bool,
  pageViewId: P.string,
  deviceType: P.string,
};

KetoStructuredCard.defaultProps = {
  productOverride: { heading: '', ctaText: '' },
  footerOptions: {},
  badgeProps: { badgeText: '' },
};

export default KetoStructuredCard;
